/**
 * PROTOCOL_REGISTRY — the ONE statement of which agent protocols and
 * discovery surfaces Sill speaks, and how far (ADR-0213).
 *
 * Every surface that names a protocol renders from here: Settings →
 * Endpoint, the onboarding Protocol Stack, the docs `reference/protocols`
 * table (generated), and marketing checks. Two hard-coded lists drifted
 * apart within a day of each other (x402 PLANNED vs DEFERRED; A2A v0.3
 * vs the card's a2a/1.2); this file exists so that cannot happen again.
 *
 * The truth it encodes is pinned by tests against the code that enforces
 * it: `apps/edge/test/protocol-registry-pin.test.ts` asserts that the
 * mandate protocols marked `live` are exactly the edge PROTOCOL_ALLOWLIST.
 *
 * Vocabulary (five words, founder-approved 2026-09-25):
 *   live               shipped; admitted by the production verifier or
 *                      served by the edge. `bound` narrows the claim
 *                      ("agent card only", "boundary").
 *   built_not_enabled  code merged and security-reviewed, gated off by a
 *                      named flag.
 *   roadmap            designed (an ADR exists), no code.
 *   deferred           no code, no timeline.
 *   not_started        nothing yet (used for surfaces we track but do not
 *                      speak, e.g. signed-request verification).
 */
export const PROTOCOL_REGISTRY = [
    {
        id: 'mcp',
        name: 'MCP',
        long_name: 'Model Context Protocol',
        vendor: 'Anthropic',
        kind: 'mandate',
        status: 'live',
        version: '2025-06-18',
        bound: null,
        gate: null,
        description: 'Per-site MCP server: initialize, tools/list and audited tools/call over Streamable HTTP. Skills are the tools, and tools/list responses carry a signature anyone can verify with the public Sill keys.',
        summary: 'Your skills as tools agents can call. Every call is recorded and signed.',
        spec_url: 'https://modelcontextprotocol.io',
        adr: 'ADR-0014',
        docs_path: '/discovery/mcp-server/',
        notify: false,
    },
    {
        id: 'a2a',
        name: 'A2A',
        long_name: 'Agent-to-Agent',
        vendor: 'Google',
        kind: 'mandate',
        status: 'live',
        // ADR-0253 S4 — spec-valid A2A protocol version (A2A §A.2.1
        // examples). The card advertises `'1.0'` as the preferred form
        // alongside a `'0.3'` fallback entry at the same URL.
        version: '1.0',
        bound: 'agent card + messaging',
        gate: null,
        description: 'A signed, A2A-shaped agent card per verified site, plus a per-site messaging endpoint. Agents can send a message naming one of your skills, read the resulting task, or cancel it. The messaging endpoint is live but not yet advertised on the agent card.',
        summary: 'A signed agent card for your site, plus messaging that names one of your skills.',
        spec_url: 'https://a2a-protocol.org',
        adr: 'ADR-0116',
        docs_path: '/discovery/agent-card/',
        notify: false,
    },
    {
        id: 'ap2',
        name: 'AP2',
        long_name: 'Agent Payments Protocol',
        vendor: 'Google',
        kind: 'mandate',
        status: 'live',
        version: null,
        bound: 'boundary',
        gate: null,
        description: 'Signed AP2 mandates are checked in full before anything is charged: a shopper-signed mandate’s signature is verified against the shopper’s key, and the cart total and currency feed the policy engine and settle on your connected rail. Inside UCP checkout, AP2 runs as the mandates extension — every cart Sill prices is signed on your behalf, and the shopper-approved mandate is checked against those signed terms.',
        summary: 'Signed payment mandates are checked against your rules before anything is charged.',
        spec_url: 'https://ap2-protocol.org',
        adr: 'ADR-0101',
        docs_path: '/transactional/signed-mandates/',
        notify: false,
    },
    {
        id: 'ard',
        name: 'ARD',
        long_name: 'Agentic Resource Discovery',
        vendor: 'ARD',
        kind: 'discovery',
        status: 'live',
        version: '1.0',
        bound: null,
        gate: null,
        description: 'A signed ai-catalog.json per site (did:web identity, detached ed25519 JWS), served at the edge.',
        summary: 'A signed catalog file that tells agents what your site offers.',
        spec_url: null,
        adr: 'ADR-0124',
        docs_path: '/discovery/ard-catalog/',
        notify: false,
    },
    {
        id: 'llms_txt',
        name: 'llms.txt',
        long_name: 'llms.txt',
        vendor: 'llmstxt.org',
        kind: 'discovery',
        status: 'live',
        version: null,
        bound: null,
        gate: null,
        description: 'A live llms.txt per site, generated from your skills and endpoints.',
        summary: 'A plain-text guide for agents, built from your skills.',
        spec_url: 'https://llmstxt.org',
        adr: 'ADR-0206',
        docs_path: '/discovery/llms-txt/',
        notify: false,
    },
    {
        id: 'agent_gateway',
        name: 'HTML gateway',
        long_name: 'HTML agent gateway (/agent)',
        vendor: 'Sill',
        kind: 'discovery',
        status: 'live',
        version: null,
        bound: null,
        gate: null,
        description: 'An HTML page for runtimes that read pages rather than JSON, linking your card and MCP server.',
        summary: 'A readable page at /agent that lists what agents can do on your site.',
        spec_url: null,
        adr: 'ADR-0208',
        docs_path: '/discovery/agent-page/',
        notify: false,
    },
    {
        id: 'jwks',
        name: 'JWKS',
        long_name: 'Public signing keys',
        vendor: 'RFC 7517',
        kind: 'discovery',
        status: 'live',
        version: null,
        bound: null,
        gate: null,
        description: 'Public keys at edge.sill.so/.well-known/jwks.json so anyone can verify Sill signatures.',
        summary: 'The public keys anyone can use to check Sill\'s signatures.',
        spec_url: 'https://www.rfc-editor.org/rfc/rfc7517',
        adr: 'ADR-0128',
        docs_path: '/verification/public-jwks/',
        notify: false,
    },
    {
        id: 'acp',
        name: 'ACP',
        long_name: 'Agentic Commerce Protocol',
        vendor: 'Stripe (with OpenAI)',
        kind: 'mandate',
        status: 'built_not_enabled',
        version: '2026-04-17',
        bound: null,
        gate: 'ACP_PROTOCOL_ENABLED (edge secret, off) · Stripe-side token sourcing (ADR-0137)',
        description: 'Shared Payment Token checkout is built but not available to merchants yet. The discovery half of ACP is served today via your catalog and agent card.',
        summary: 'Shared Payment Token checkout. Built, not available to merchants yet.',
        spec_url: 'https://docs.stripe.com/agentic-commerce',
        adr: 'ADR-0130',
        docs_path: '/reference/protocols/#acp--agentic-commerce-protocol',
        notify: true,
    },
    {
        id: 'ucp',
        name: 'UCP',
        long_name: 'Universal Commerce Protocol',
        vendor: 'Google × Shopify',
        kind: 'endpoint',
        status: 'live',
        version: '2026-08-25 and 2026-04-08',
        bound: 'checkout + orders, Stripe rail',
        gate: null,
        description: 'Sill is your store’s UCP endpoint: it publishes your profile at /.well-known/ucp, runs signed checkouts over MCP, prices carts from your catalog under your guardrails, sends orders to human review when a rule asks, takes payment through Google Pay to your Stripe account, records each decision, and reports orders back through signed webhooks. Supports UCP 2026-08-25 and 2026-04-08. Discount codes drawn from your Stripe promotion codes can be enabled per site (off by default). Checkout is advertised once Stripe is connected with its rail on, the site settles through Stripe, a policy is published, and (with a live Stripe account) a Google Pay merchant ID is set. Being listed on Google’s shopping surfaces requires Google’s merchant approval.',
        summary: 'Agent checkout priced from your catalog under your rules, paid through Stripe.',
        spec_url: 'https://ucp.dev',
        adr: 'ADR-0215',
        docs_path: '/transactional/ucp-checkout/',
        notify: false,
    },
    {
        id: 'web_bot_auth',
        name: 'Web Bot Auth',
        long_name: 'Signed agent requests (RFC 9421)',
        vendor: 'IETF',
        kind: 'identity',
        status: 'live',
        version: 'draft-ietf-webbotauth-httpsig-protocol-00',
        bound: 'Worker and plugin installs',
        gate: null,
        description: 'Checks the signature some agents attach to their requests against the signer’s published key, so a visit is a verified identity rather than a user-agent guess. Agents that sign today include ChatGPT agent mode and Exa.',
        summary: 'Checks signed agent requests, so a visit is a verified identity, not a guess.',
        spec_url: 'https://datatracker.ietf.org/wg/webbotauth/',
        adr: null,
        docs_path: '/reference/protocols/#web-bot-auth--signed-agent-requests',
        notify: false,
    },
    {
        id: 'pap',
        name: 'PAP',
        long_name: 'Personal Agent Protocol (Poppy)',
        vendor: 'Meta × Sierra',
        kind: 'endpoint',
        status: 'live',
        version: 'draft 0.2',
        bound: 'guest sessions',
        gate: null,
        description: 'Sill publishes your site’s PAP discovery file, issues guest sessions to a shopper’s personal agent, and lets that agent use your skills over MCP. Guest sessions only — shoppers are not signed in to their accounts, and PAP does not cover payments.',
        summary: 'Guest sessions for a shopper\'s personal agent, using your skills. No sign-in, no payments.',
        spec_url: 'https://personalagentprotocol.org/docs/spec',
        adr: 'ADR-0252',
        docs_path: '/reference/personal-agent-protocol/',
        notify: false,
    },
    {
        id: 'x402',
        name: 'x402',
        long_name: 'x402',
        vendor: 'Coinbase · Stripe',
        kind: 'mandate',
        status: 'deferred',
        version: null,
        bound: null,
        gate: null,
        description: 'On-chain HTTP-native payments. No code, no timeline.',
        summary: 'On-chain payments. Not scheduled.',
        spec_url: 'https://x402.org',
        adr: null,
        docs_path: '/reference/protocols/#x402--on-chain-http-payments',
        notify: false,
    },
];
/** Mandate protocol ids the registry says are live — must equal the edge allow-list. */
export function liveMandateProtocolIds() {
    return PROTOCOL_REGISTRY.filter((p) => p.kind === 'mandate' && p.status === 'live').map((p) => p.id);
}
export function protocolEntry(id) {
    const e = PROTOCOL_REGISTRY.find((p) => p.id === id);
    if (e === undefined)
        throw new Error(`unknown protocol ${id}`);
    return e;
}
/** Status word as shown in the UI and docs. */
export const PROTOCOL_STATUS_LABEL = {
    live: 'LIVE',
    built_not_enabled: 'BUILT · NOT ENABLED',
    roadmap: 'ROADMAP',
    deferred: 'DEFERRED',
    not_started: 'NOT STARTED',
};
/** "LIVE · agent card only" etc. */
export function protocolStatusText(e) {
    const base = PROTOCOL_STATUS_LABEL[e.status];
    return e.bound !== null ? `${base} · ${e.bound}` : base;
}
//# sourceMappingURL=protocols.js.map